export type UploadCheckpoint = {
  version: 1;
  submissionId: string;
  draftKey: string;
  sessionKey: string;
  completeKey: string;
  publishKey: string;
  draftId?: string;
  sessionId?: string;
  notes: string;
  mediaType: string;
  fingerprint: {
    sourceIdentity: string;
    sizeBytes: string;
    lastModified: string;
    providerGeneration: null;
    sha256: string;
  };
};

export function uploadJournalKey(
  userId: string,
  tripId: string,
  deliverableId: string,
  nodeId: string,
  sha: string,
) {
  return `trip:revision-upload:v1:${userId}:${tripId}:${deliverableId}:${nodeId}:${sha}`;
}

/** Persist before every server mutation so a lost response can be replayed with the same key/body. */
export function saveCheckpoint(key: string, checkpoint: UploadCheckpoint): void {
  try {
    localStorage.setItem(key, JSON.stringify(checkpoint));
  } catch {
    throw new Error(
      "Your browser could not save upload progress. Enable site storage and try again.",
    );
  }
}

export function readCheckpoint(key: string, size: string, sha: string): UploadCheckpoint | null {
  const raw = localStorage.getItem(key);
  if (raw === null) return null;
  try {
    const c = JSON.parse(raw) as UploadCheckpoint;
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
    if (
      c.version !== 1 ||
      typeof c.notes !== "string" ||
      typeof c.mediaType !== "string" ||
      ![c.submissionId, c.draftKey, c.sessionKey, c.completeKey, c.publishKey].every(
        (s) => typeof s === "string" && uuid.test(s),
      ) ||
      (c.draftId !== undefined && !uuid.test(c.draftId)) ||
      (c.sessionId !== undefined && (!c.draftId || !uuid.test(c.sessionId))) ||
      c.fingerprint.sizeBytes !== size ||
      c.fingerprint.sha256 !== sha ||
      typeof c.fingerprint.sourceIdentity !== "string" ||
      !Number.isFinite(Date.parse(c.fingerprint.lastModified)) ||
      c.fingerprint.providerGeneration !== null
    ) {
      throw new Error("Invalid checkpoint");
    }
    return c;
  } catch {
    // Do not silently create a second revision if a recoverable upload record is damaged.
    throw new Error(
      "Saved upload progress is damaged. Clear this site's saved upload data before retrying.",
    );
  }
}
